import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useDrawer } from "../components/drawer/DrawerProvider";
import DetailsTicketPopup from "../components/detailsTicketPopup/DetailsTicketPopup";
import shareIcon from "../assets/icons/share-icon.svg";
import api from "../api/api";
import getOrigins from "../utils/getOrigins";
import cookieService from "../services/cookieService";
import AppConstants from "../constants/AppConstants";
import Breadcrumbs from "../components/breadcrumbs/Breadcrumbs";
import ParkImages from "../components/park/ParkImages";
import RideCart from "./RideCart";
import '../assets/css/ParkDetails.css'

export default function ParkDetails() {
  const { id } = useParams();

  const [park, setPark] = useState([]);
  const [rides, setRides] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchEntryTickets, setBranchEntryTickets] = useState([]);
  const { openDrawer, closeDrawer } = useDrawer();

  const handleOpenDrawer = useCallback(() => {
    const isLoggedIn = cookieService.get(AppConstants.CS_KEY.ACCESS_TOKEN);
    if (isLoggedIn) {
      openDrawer(
        <DetailsTicketPopup park={park} branchId={selectedBranch?.id} />,
        "Buy Tickets"
      );
    } else {
      openDrawer(
        <iframe
          src={`${getOrigins(window)}/login?embed=true`}
          height="100%"
          width="100%"
          title="Login"
        />,
        ""
      );

      window.addEventListener("message", (event) => {
        const { type, payload } = event.data || {};
        if (type === "MY_IFRAME_EVENT") {
          if (payload?.isAuthenticated === true) {
            openDrawer(
              <DetailsTicketPopup park={park} branchId={selectedBranch?.id} />,
              "Buy Tickets"
            );
          }
        }
      });
    }
  }, [openDrawer, park, selectedBranch?.id]);

  const fetch = useCallback(async () => {
    const res = await api.getPark({ id });
    if (res?.status === 200) {
      setPark(res.data.data);
      setRides(res.data.data.rides.slice(0, 6));
      setSelectedBranch(res.data.data?.branches?.[0]);
    }
  }, [id]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const newBranchEntryTickets = park?.entry_tickets?.filter(
      (ticket) => ticket.branch_id === +selectedBranch.id
    );
    setBranchEntryTickets(newBranchEntryTickets);
  }, [selectedBranch, park?.entry_tickets]);

  function handleBranchChange(e) {
    const newSelectedBranch = park?.branches?.find(
      (branch) => branch.id === +e.target.value
    );
    setSelectedBranch(newSelectedBranch);
  }

  const copyToClipboard = async () => {
    const currentUrl = `${window.location.href}`;
    try {
      await navigator.clipboard.writeText(currentUrl);
      Swal.fire({
        title: "Link copied!",
        text: "You can now share it on your favorite social media",
        icon: "success",
        confirmButtonColor: "#079d49",
        confirmButtonText: "Got it",
      });
    } catch (err) {
      Swal.fire({
        title: "Copy failed",
        text: "Sorry, the link could not be copied",
        icon: "error",
        confirmButtonColor: "#d33",
        confirmButtonText: "Got it",
      });
    }
  }

  useEffect(
    () => () => {
      closeDrawer();
    },
    [closeDrawer]
  );

  return (
    <div className="park-details-container">
      <Breadcrumbs activeId={3} />

      <div className="custom-container">
        {/* Image Gallery */}
        <ParkImages rides={park?.rides} logo={park.logo} name={park.name} />

        {/* Main Content */}
        <div className="park-content">
          {/* Left Column */}
          <div>
            <h2 className="park-title">{park.name}</h2>
            <p className="park-details-text">Details</p>
            <p className="park-description">{park.description}</p>

            <div className="park-location">
              <p>Locations ({park?.branches?.length} available):</p>
              <div>
                {park?.branches?.map((branch) => (
                  <span key={branch.id}>
                    {branch.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="park-ride">
              <p>Rides ({park.ride_count} available):</p>
              <div className="row g-4 g-md-1 g-lg-4">
                {rides?.map((ride) => <RideCart key={ride.id} ride={ride} />)}
              </div>
            </div>

            {park?.rides?.length > rides?.length && (
              <button
                type="button"
                className="park-see-more"
                onClick={() => setRides(park.rides)}
              >
                SEE MORE
              </button>
            )}
          </div>

          {/* Right Column */}
          <div className="park-white-container">
            <div className="select-container">
              <p className="park-select-location">Select Location</p>
              <select
                className="form-select w-100"
                id="locationSelect"
                onChange={handleBranchChange}
              >
                {park?.branches?.map((branch) => (
                  <option value={branch.id} key={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <div className="button-container">
                <button
                  type="button"
                  className="btn btn-success w-75 fw-bold"
                  onClick={handleOpenDrawer}
                >
                  BUY TICKETS
                </button>
                <button
                  type="button"
                  className="bg-white border-0 p-0 m-0"
                  onClick={copyToClipboard}
                >
                  <img src={shareIcon} alt="share-icon" />
                </button>
              </div>
            </div>
            <div className="entry-ticket-container">
              <div className="info-box mobile-off">
                <div className="left-info">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M2.00488 9.5V4C2.00488 3.44771 2.4526 3 3.00488 3H21.0049C21.5572 3 22.0049 3.44771 22.0049 4V9.5C20.6242 9.5 19.5049 10.6193 19.5049 12C19.5049 13.3807 20.6242 14.5 22.0049 14.5V20C22.0049 20.5523 21.5572 21 21.0049 21H3.00488C2.4526 21 2.00488 20.5523 2.00488 20V14.5C3.38559 14.5 4.50488 13.3807 4.50488 12C4.50488 10.6193 3.38559 9.5 2.00488 9.5ZM14.0049 5H4.00488V7.96776C5.4866 8.70411 6.50488 10.2331 6.50488 12C6.50488 13.7668 5.4866 15.2959 4.00488 16.0322V19H14.0049V5ZM16.0049 5V19H20.0049V16.0322C18.5232 15.2959 17.5049 13.7668 17.5049 12C17.5049 10.2331 18.5232 8.70411 20.0049 7.96776V5H16.0049Z" fill="#079D49" />
                  </svg>
                </div>
                <h6>Entry Tickets</h6>
              </div>
              {branchEntryTickets?.map((ticket) => (
                <div key={ticket.id} className="info-box mobile-off">
                  <div className="left-info" />
                  <div className="right-info">
                    <p>{ticket.name}</p>
                    <p>BDT {ticket.price}</p>
                  </div>
                </div>
              ))}
              <div className="info-box">
                <div className="left-info">
                  <svg className="mobile-off" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M11.95 4.05862C7.59171 4.05862 4.05862 7.59171 4.05862 11.95C4.05862 16.3083 7.59171 19.8414 11.95 19.8414C16.3083 19.8414 19.8414 16.3083 19.8414 11.95C19.8414 7.59171 16.3083 4.05862 11.95 4.05862ZM2 11.95C2 6.45477 6.45477 2 11.95 2C17.4452 2 21.9 6.45477 21.9 11.95C21.9 17.4452 17.4452 21.9 11.95 21.9C6.45477 21.9 2 17.4452 2 11.95Z" fill="#079D49" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M11.9243 5.59961C12.49 5.59961 12.9486 6.06068 12.9486 6.62943V11.3481L15.9334 12.8485C16.4394 13.1028 16.6445 13.7214 16.3915 14.2301C16.1385 14.7388 15.5232 14.945 15.0172 14.6907L11.4661 12.9056C11.1191 12.7312 10.8999 12.3746 10.8999 11.9845V6.62943C10.8999 6.06068 11.3585 5.59961 11.9243 5.59961Z" fill="#079D49" />
                  </svg>
                  <svg className="mobile-on" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M6.99984 11.9587C9.73825 11.9587 11.9582 9.73874 11.9582 7.00033C11.9582 4.26191 9.73825 2.04199 6.99984 2.04199C4.26143 2.04199 2.0415 4.26191 2.0415 7.00033C2.0415 9.73874 4.26143 11.9587 6.99984 11.9587Z" stroke="#969696" />
                    <path d="M9.625 7.00065H7.14583C7.10716 7.00065 7.07006 6.98529 7.04271 6.95794C7.01536 6.93059 7 6.8935 7 6.85482V4.95898" stroke="#969696" strokeLinecap="round" />
                  </svg>
                </div>
                <p>Time: {selectedBranch?.start_time} - {selectedBranch?.end_time}</p>
              </div>
              <div className="info-box">
                <div className="left-info">
                  <svg className="mobile-off" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 19.8995L16.9497 14.9497C19.6834 12.2161 19.6834 7.78392 16.9497 5.05025C14.2161 2.31658 9.78392 2.31658 7.05025 5.05025C4.31658 7.78392 4.31658 12.2161 7.05025 14.9497L12 19.8995ZM12 22.7279L5.63604 16.364C2.12132 12.8492 2.12132 7.15076 5.63604 3.63604C9.15076 0.12132 14.8492 0.12132 18.364 3.63604C21.8787 7.15076 21.8787 12.8492 18.364 16.364L12 22.7279ZM12 12C13.1046 12 14 11.1046 14 10C14 8.89543 13.1046 8 12 8C10.8954 8 10 8.89543 10 10C10 11.1046 10.8954 12 12 12ZM12 14C9.79086 14 8 12.2091 8 10C8 7.79086 9.79086 6 12 6C14.2091 6 16 7.79086 16 10C16 12.2091 14.2091 14 12 14Z" fill="#079D49" />
                  </svg>
                  <svg className="mobile-on" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 7.58398C7.9665 7.58398 8.75 6.80048 8.75 5.83398C8.75 4.86749 7.9665 4.08398 7 4.08398C6.0335 4.08398 5.25 4.86749 5.25 5.83398C5.25 6.80048 6.0335 7.58398 7 7.58398Z" stroke="#969696" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7.00016 1.16699C5.76249 1.16699 4.5755 1.65866 3.70033 2.53383C2.82516 3.409 2.3335 4.59598 2.3335 5.83366C2.3335 6.93733 2.568 7.65949 3.2085 8.45866L7.00016 12.8337L10.7918 8.45866C11.4323 7.65949 11.6668 6.93733 11.6668 5.83366C11.6668 4.59598 11.1752 3.409 10.3 2.53383C9.42482 1.65866 8.23784 1.16699 7.00016 1.16699Z" stroke="#969696" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p>{selectedBranch?.address}</p>
              </div>
              <div className="ride-mobile-container">
                <h5>Rides</h5>
                <div className="mobile-ride-info">
                  {rides?.map((ride) => <RideCart key={ride.id} ride={ride} mobile />)}
                </div>
              </div>
            </div>
            <div className="map-container">
              <iframe
                className="w-100"
                height="200"
                src={selectedBranch?.googleMapUrl}
                title="Location Map"
                loading="lazy"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mobile-book-ticket-container">
        <button
          type="button"
          onClick={handleOpenDrawer}
        >
          Book Tickets
        </button>
      </div>
    </div>
  );
}
